import { Link } from 'react-router-dom';
import type { Product } from '@shopgnd/shared';
import { MediaImage } from '@/components/MediaImage';
import { StarRating } from '@/components/StarRating';
import { VendorName } from '@/components/VendorName';
import { useVendors } from '@/hooks/useVendors';
import { useCartStore } from '@/stores/cartStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useWishlistStore } from '@/stores/wishlistStore';
import { useCurrencyStore } from '@/stores/currencyStore';
import { convertCents, formatMoney } from '@/lib/currency';
import { productPath } from '@/lib/productUrl';

export function ProductCard({
  product,
  hideVendor = false,
  hideListPrice = false,
  showAddToCart = true,
}: {
  product: Product;
  hideVendor?: boolean;
  hideListPrice?: boolean;
  showAddToCart?: boolean;
}) {
  const { vendors } = useVendors();
  const img = product.images[0];
  const sponsored = (product.sponsoredRank ?? 0) > 0;
  const vendor = vendors.find((v) => v.id === product.vendorId);
  const rating = vendor?.ratingAvg ?? 0;
  const count = vendor?.ratingCount ?? 0;
  const add = useCartStore((s) => s.add);
  const push = useNotificationStore((s) => s.push);
  const toggleWishlist = useWishlistStore((s) => s.toggle);
  const isWishlisted = useWishlistStore((s) => s.has(product.id));
  const displayCurrency = useCurrencyStore((s) => s.currency);
  const hasVariants = (product.variants?.length ?? 0) > 0;
  const inStock = product.stock > 0 || product.allowBackorders === true;
  const canQuickAdd = !hasVariants && inStock;
  const displayPrice = formatMoney(
    convertCents(product.priceCents, product.currency, displayCurrency),
    displayCurrency,
  );
  const grenadianOwned = !!vendor && vendor.businessName.toLowerCase().includes('spice');
  const islandPickup = product.parish === 'Saint George' || product.parish === 'Saint Andrew';
  const productUrl = productPath(product);

  return (
    <div className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700/60 dark:bg-slate-900">
      {/* Image */}
      <div className="relative overflow-hidden bg-slate-50 dark:bg-slate-800/50">
        {sponsored ? (
          <span className="pointer-events-none absolute left-2.5 top-2.5 z-10 rounded-md bg-amber-400/90 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-slate-900 backdrop-blur-sm">
            Sponsored
          </span>
        ) : null}
        <Link to={productUrl} className="block" tabIndex={-1} aria-hidden="true">
          <div className="aspect-square overflow-hidden">
            {img ? (
              <MediaImage
                src={img}
                alt=""
                dimensionPreset="square"
                className="h-full w-full object-contain p-3 transition-transform duration-300 group-hover:scale-[1.04] mix-blend-multiply dark:mix-blend-normal"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-4xl text-slate-200 dark:text-slate-700">
                ·
              </div>
            )}
          </div>

          {!inStock && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-slate-900/60">
              <span className="rounded-full bg-slate-900/80 px-3 py-1 text-xs font-semibold text-white dark:bg-white/80 dark:text-slate-900">
                Out of stock
              </span>
            </div>
          )}
        </Link>

        <button
          type="button"
          aria-label={isWishlisted ? 'Remove from wishlist' : 'Save to wishlist'}
          onClick={() => toggleWishlist(product.id)}
          className="absolute right-2.5 top-2.5 z-10 min-h-11 min-w-11 rounded-full bg-white/90 p-2.5 shadow-sm transition hover:bg-white dark:bg-slate-800/90 dark:hover:bg-slate-800"
        >
          <svg
            className={`mx-auto h-4 w-4 transition ${isWishlisted ? 'fill-grenada-red text-grenada-red' : 'fill-none text-slate-400 hover:text-slate-600'}`}
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-1 px-3 pb-1 pt-2.5">
        <Link to={productUrl} className="group/title outline-none">
          <p className="line-clamp-2 text-sm font-medium leading-snug text-slate-800 transition group-hover/title:text-brand-700 dark:text-slate-100 dark:group-hover/title:text-brand-300">
            {product.title}
          </p>
        </Link>

        {count > 0 ? (
          <StarRating rating={rating} count={count} />
        ) : (
          <p className="text-xs text-slate-600 dark:text-slate-400">No reviews yet</p>
        )}

        <p className="mt-0.5 text-base font-semibold text-slate-900 dark:text-white">
          {displayPrice}
        </p>

        {/* Badges */}
        {(product.isLocallyMade || grenadianOwned || islandPickup) && (
          <div className="flex flex-wrap gap-1">
            {product.isLocallyMade && (
              <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white">
                🇬🇩 Grenada Made
              </span>
            )}
            {grenadianOwned && !product.isLocallyMade && (
              <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700 dark:bg-brand-950 dark:text-brand-300">
                🇬🇩 Local vendor
              </span>
            )}
            {islandPickup && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                Pickup available
              </span>
            )}
          </div>
        )}
      </div>

      {/* Vendor */}
      {!hideVendor && vendor ? (
        <p className="px-3 pb-1 text-[11px] text-slate-400 dark:text-slate-500">
          <VendorName
            vendor={vendor}
            badgeSize="sm"
            className="text-slate-500 hover:text-brand-700 hover:underline dark:text-slate-400 dark:hover:text-brand-400"
          />
        </p>
      ) : null}

      {/* Add to cart */}
      {showAddToCart ? (
        <div className="px-3 pb-3 pt-1">
          {hasVariants ? (
            <p className="py-2 text-center text-[11px] text-text-secondary">Select options</p>
          ) : (
            <button
              type="button"
              disabled={!canQuickAdd}
              onClick={() => {
                add({
                  productId: product.id,
                  quantity: 1,
                  title: product.title,
                  priceCents: product.priceCents,
                  image: product.images[0],
                  vendorId: product.vendorId,
                });
                push({ title: 'Added to cart', tone: 'success' });
              }}
              className="w-full rounded-lg bg-brand-700 py-2 text-xs font-semibold text-white transition hover:bg-brand-800 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-brand-600 dark:hover:bg-brand-700"
            >
              {product.allowBackorders && product.stock <= 0 ? 'Order Now' : 'Add to cart'}
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}
